import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { calculateYTG, calculateYTGPercentage, copySummaryTableToClipboard, getOrderedMonthNames } from '../Utilities/tableUtils';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import './SkuBaseForecastTable.css';

const OutputsSummaryTable = ({ summaryData, view, startMonth, selectedForecast, aggregationPeriod = "monthly", showActuals = false, loadingActuals = false, showCompare = false }) => {
    // Use period names from API response if available, otherwise fall back to month names
    const basePeriodNames = summaryData?.periodNames || getOrderedMonthNames(startMonth);
    
    // Format period names with week numbers if it's weekly aggregation
    const periodNames = aggregationPeriod === "weekly" 
        ? basePeriodNames.map((period, index) => {
            // Check if this looks like a date (DD-MM-YYYY or DD-MM-YY format) 
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period; // Keep YTG, FY, etc. as is
        })
        : basePeriodNames;

    const currentYear = new Date().getFullYear();
    const selectedYear = selectedForecast?.startDate ? new Date(selectedForecast.startDate).getFullYear() : currentYear;

    const currencyPrefix = ['grossRev', 'netRev', 'fixedSpend', 'grossProfit'].includes(view) ? '£' : '';
    const percentPostfix = view === 'grossProfitPercent' ? '%' : '';

    const formatValue = (value) => {
        if (value === undefined || value === null) return 'N/A';
        const roundedValue = Math.round(value * 100) / 100;
        const displayValue = roundedValue % 1 === 0 ? roundedValue.toString() : roundedValue.toFixed(2);
        return `${currencyPrefix}${displayValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${percentPostfix}`;
    };

    const formatVariance = (value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        const formatted = formatValue(value);
        return value > 0 ? `+${formatted}` : formatted;
    };

    const actualKey = view === 'units' ? 'actualUnits' 
        : view === 'grossRev' ? 'actualGrossRev' 
        : view === 'triggers' ? 'actualTriggers'
        : view === 'cogs' ? 'actualCogs'
        : view === 'logs' ? 'actualLogs'
        : null;

    const getActualValue = (entity, index) => {
        if (!actualKey || !entity) {
            return null;
        }
        const series = entity[actualKey];
        if (!Array.isArray(series)) {
            return null;
        }
        const value = series[index];
        return value === undefined ? null : value;
    };

    const renderValueCell = (forecastValue, actualValue) => {
        if (!showActuals || !actualKey) {
            return formatValue(forecastValue);
        }

        const forecastIsNumber = typeof forecastValue === 'number' && !Number.isNaN(forecastValue);
        const safeForecast = forecastIsNumber ? forecastValue : 0;
        const hasActual = actualValue !== null && actualValue !== undefined;
        const variance = hasActual && forecastIsNumber ? actualValue - safeForecast : null;

        if (!showCompare) {
            const displayValue = hasActual ? actualValue : forecastValue;
            return (
                <Typography 
                    variant="body2"
                    sx={{ 
                        color: hasActual ? 'primary.main' : 'inherit',
                        fontWeight: hasActual ? 'bold' : 'normal'
                    }}
                >
                    {loadingActuals ? '…' : formatValue(displayValue)}
                </Typography>
            );
        }

        const actualDisplay = loadingActuals
            ? '…'
            : hasActual
                ? formatValue(actualValue)
                : '—';
        const varianceDisplay = loadingActuals
            ? '…'
            : variance !== null
                ? formatVariance(variance)
                : '—';
        const varianceColor = variance === null || loadingActuals
            ? 'text.secondary'
            : variance > 0
                ? 'success.main'
                : variance < 0
                    ? 'error.main'
                    : 'text.secondary';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                <Typography
                    variant="caption"
                    component="span"
                    color="text.secondary"
                    sx={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                >
                    {formatValue(forecastValue)}
                </Typography>
                <Typography 
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 'bold' }}
                >
                    {actualDisplay}
                </Typography>
                <Typography
                    variant="caption"
                    component="span"
                    sx={{ display: 'block', whiteSpace: 'nowrap', color: varianceColor }}
                >
                    {varianceDisplay}
                </Typography>
            </Box>
        );
    };



    const copyToClipboard = async () => {
        await copySummaryTableToClipboard(summaryData, view, startMonth, selectedYear, currentYear, aggregationPeriod, showActuals);
    };

    // ✅ Early return if summaryData is missing or malformed
    if (!summaryData || !summaryData.channels || !summaryData.totals) {
        return <div>No data available</div>;
    }

    // Calculate number of periods (excluding FY total)
    const periodCount = (summaryData.totals?.[view] || []).length - 1;
    const fyIndex = periodCount;

    return (
        <TableContainer 
            component={Paper} 
            sx={{ 
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto' 
            }}
        >
            <Table aria-label="account summary table" stickyHeader>
                <TableHead>
                    <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                        <TableCell sx={{
                            backgroundColor: (theme) => theme.palette.background.paper,
                            zIndex: 1
                        }}>
                            <Tooltip title="Copy table to clipboard">
                                <IconButton
                                    onClick={copyToClipboard}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                        {periodNames.slice(0, -1).map((period, index) => {
                            const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                            return (
                                <TableCell 
                                    key={`${period}-${index}`} 
                                    align="right" 
                                    sx={{ 
                                        whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                        backgroundColor: (theme) => theme.palette.background.paper,
                                        zIndex: 1
                                    }}
                                    data-week-header={isWeekHeader}
                                >
                                    {period.toUpperCase()}
                                </TableCell>
                            );
                        })}
                        <TableCell align="right" sx={{ 
                            fontWeight: 'bold', 
                            whiteSpace: 'nowrap',
                            backgroundColor: (theme) => theme.palette.background.paper,
                            zIndex: 1
                        }}>
                            YTG
                        </TableCell>
                        <TableCell align="right" sx={{ 
                            fontWeight: 'bold', 
                            whiteSpace: 'nowrap',
                            backgroundColor: (theme) => theme.palette.background.paper,
                            zIndex: 1
                        }}>
                            {periodNames[periodNames.length - 1]?.toUpperCase() || 'FY'}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* ✅ TOTALS Row (from API) */}
                    <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                        <TableCell component="th" scope="row">TOTAL</TableCell>
                        {(summaryData.totals?.[view] || []).slice(0, periodCount).map((total, index) => (
                            <TableCell align="right" key={index}>
                                {renderValueCell(total, getActualValue(summaryData.totals, index))}
                            </TableCell>
                        ))}
                        <TableCell align="right">
                            {renderValueCell(
                                view === 'grossProfitPercent'
                                    ? calculateYTGPercentage(summaryData.totals?.grossProfit, summaryData.totals?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
                                    : calculateYTG(summaryData.totals?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod),
                                null
                            )}
                        </TableCell>
                        <TableCell align="right">
                            {renderValueCell(
                                (summaryData.totals?.[view] || [])[fyIndex],
                                getActualValue(summaryData.totals, fyIndex)
                            )}
                        </TableCell>
                    </TableRow>

                    {/* ✅ Channels and Data Rows */}
                    {summaryData.channels?.map((channel) => (
                        <React.Fragment key={channel.channel}>
                            {/* ✅ Channel Totals (from API) */}
                            <TableRow sx={{ '& > *': { fontWeight: 'bold', backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                                <TableCell component="th" scope="row">{channel.channel}</TableCell>
                                {(channel.channelTotal?.[view] || []).slice(0, periodCount).map((total, index) => (
                                    <TableCell align="right" key={index}>
                                        {renderValueCell(total, getActualValue(channel.channelTotal, index))}
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    {renderValueCell(
                                        view === 'grossProfitPercent'
                                            ? calculateYTGPercentage(channel.channelTotal?.grossProfit, channel.channelTotal?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
                                            : calculateYTG(channel.channelTotal?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod),
                                        null
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {renderValueCell(
                                        (channel.channelTotal?.[view] || [])[fyIndex],
                                        getActualValue(channel.channelTotal, fyIndex)
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* ✅ Customer Rows */}
                            {channel.customers?.map((customer) => (
                                <TableRow key={customer.name}>
                                    <TableCell component="th" scope="row">{customer.name}</TableCell>
                                    {(customer?.[view] || []).slice(0, periodCount).map((value, index) => (
                                        <TableCell align="right" key={index}>
                                            {renderValueCell(value, getActualValue(customer, index))}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">
                                        {renderValueCell(
                                            view === 'grossProfitPercent'
                                                ? calculateYTGPercentage(customer?.grossProfit, customer?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod)
                                                : calculateYTG(customer?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod),
                                            null
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {renderValueCell(
                                            (customer?.[view] || [])[fyIndex],
                                            getActualValue(customer, fyIndex)
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OutputsSummaryTable;
